import { useId, useMemo, useState } from 'react';
import { Check, Copy, Loader2, Mail, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  composeErrorReport,
  errorReportMailto,
  formatReportTime,
  REPORT_EMAIL,
  type ErrorReportContext,
} from '@/lib/errorReport';
import { submitErrorReport } from '@/lib/errorReportSubmit';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: ErrorReportContext;
}

type SendState = 'idle' | 'sending' | 'sent' | 'failed';

/**
 * The report surface behind every error screen and every "Report an issue"
 * entry. One dialog, shared by the root boundary, the per-portal boundary and
 * the portal sidebars, so the wording and the attached details cannot drift.
 *
 * Sending goes to the team's queue first; if that path is unavailable the
 * person keeps their text and is offered Copy and email instead. A report
 * surface that fails whenever the app is broken would be useless exactly when
 * it matters.
 */
// Unsent text survives the dialog closing (e.g. after handing off to the mail
// app) and a fresh reference on reopen; cleared once a report is sent.
const unsentDraft = { whatHappened: '', expected: '' };

export function ErrorReportDialog({ open, onOpenChange, context }: Props) {
  const [whatHappened, setWhatHappenedState] = useState(unsentDraft.whatHappened);
  const [expected, setExpectedState] = useState(unsentDraft.expected);
  const setWhatHappened = (v: string) => { unsentDraft.whatHappened = v; setWhatHappenedState(v); };
  const setExpected = (v: string) => { unsentDraft.expected = v; setExpectedState(v); };
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [sendState, setSendState] = useState<SendState>('idle');
  const whatId = useId();
  const expectedId = useId();

  const input = useMemo(
    () => ({ whatHappened, expected }),
    [whatHappened, expected],
  );
  const mailto = useMemo(
    () => errorReportMailto(context, input),
    [context, input],
  );

  const copyDetails = async () => {
    try {
      await navigator.clipboard.writeText(composeErrorReport(context, input));
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  const send = async () => {
    setSendState('sending');
    const sent = await submitErrorReport(context, input);
    setSendState(sent ? 'sent' : 'failed');
    if (sent) { unsentDraft.whatHappened = ''; unsentDraft.expected = ''; }
  };

  // Hand off to the mail app in place: a new tab/window blurs the page, and
  // the dialog read that focus loss as a dismissal.
  const openEmail = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    window.location.href = mailto;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90dvh] w-[calc(100%-2rem)] flex-col overflow-hidden sm:max-w-lg gap-3" data-testid="error-report-dialog" onInteractOutside={(event) => event.preventDefault()} onFocusOutside={(event) => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{sendState === 'sent' ? 'Report sent' : 'Report an issue'}</DialogTitle>
          <DialogDescription>
            {sendState === 'sent'
              ? 'The team has the details and the reference below. Nothing else is needed from you.'
              : 'The reference below identifies this report. Add what you were doing, then send it to the team. Nothing is sent until you choose to send it.'}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 space-y-3 overflow-y-auto overscroll-contain">
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 rounded-md border border-border bg-muted/40 px-4 py-3 text-sm">
          <dt className="text-muted-foreground">Reference</dt>
          <dd className="font-mono font-medium tracking-wide select-all">
            {context.reference}
          </dd>
          <dt className="text-muted-foreground">Page</dt>
          <dd className="font-mono text-xs break-all">{context.route}</dd>
          <dt className="text-muted-foreground">When</dt>
          <dd className="text-xs">{formatReportTime(context.occurredAt)}</dd>
        </dl>

        {sendState === 'sent' ? (
          <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
            <p className="text-muted-foreground">
              Quote <span className="font-mono text-foreground">{context.reference}</span> if you
              contact us about this again.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor={whatId}>What happened?</Label>
              <Textarea
                id={whatId}
                value={whatHappened}
                onChange={(event) => {
                  setWhatHappened(event.target.value);
                  setCopyState('idle');
                  if (sendState === 'failed') setSendState('idle');
                }}
                placeholder="I clicked Save on the project page and the screen went blank."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={expectedId}>
                What did you expect?{' '}
                <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Textarea
                id={expectedId}
                value={expected}
                onChange={(event) => {
                  setExpected(event.target.value);
                  setCopyState('idle');
                  if (sendState === 'failed') setSendState('idle');
                }}
                rows={2}
              />
            </div>
          </>
        )}

        </div>
        <div className="min-h-4 shrink-0 text-xs text-muted-foreground" role="status">
            {copyState === 'copied' && (
              <span className="inline-flex items-center gap-1">
                <Check className="h-3.5 w-3.5" aria-hidden /> Copied
              </span>
            )}
            {copyState === 'failed' && `Copy blocked, email ${REPORT_EMAIL} instead.`}
        </div>
        <DialogFooter className="shrink-0 flex-row flex-wrap justify-end gap-2 sm:gap-2">

          {sendState === 'sent' ? (
            <Button type="button" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" className="min-w-32" onClick={() => void copyDetails()}>
                <Copy className="h-4 w-4" aria-hidden />
                Copy details
              </Button>
              <Button asChild variant="ghost">
                <a href={mailto} onClick={openEmail} aria-label={`Email ${REPORT_EMAIL}`}>
                  <Mail className="h-4 w-4" aria-hidden />
                  Email
                </a>
              </Button>
              <Button
                type="button"
                className="min-w-36"
                onClick={() => void send()}
                disabled={sendState === 'sending'}
              >
                {sendState === 'sending' ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Send className="h-4 w-4" aria-hidden />
                )}
                Send report
              </Button>
            </>
          )}
        </DialogFooter>

        {sendState === 'failed' && (
          <p className="text-xs text-destructive-text" role="alert">
            The report could not be sent. Nothing you typed is lost: copy the
            details or email {REPORT_EMAIL}.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
